// Projects Settings Page - Manage all projects in the organization
import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  Delete,
  ContentCopy,
  Code,
  CheckCircle,
  OpenInNew,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useToast } from '@/contexts/ToastContext';
import { usePermissions } from '@/hooks/usePermissions';
import { organizationsService } from '@/services/collections/organizations';
import { AddProjectDialog } from '@/components/projects/AddProjectDialog';
import { PLAN_LIMITS, type Project } from '@/types';

const editProjectSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(50, 'Name too long'),
  description: z.string().max(200, 'Description too long').optional(),
  domain: z.string().url('Must be a valid URL').optional().or(z.literal('')),
});

type EditProjectFormData = z.infer<typeof editProjectSchema>;

export const ProjectsSettingsPage = () => {
  const { organization, project: currentProject, projects, refreshProjects, setCurrentProject } =
    useOrganization();
  const { showSuccess, showError } = useToast();
  const { plan, can } = usePermissions();

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; project: Project } | null>(null);
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const planLimits = PLAN_LIMITS[plan];
  const projectLimit = planLimits.projects;
  const atLimit = projectLimit !== -1 && projects.length >= projectLimit;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditProjectFormData>({
    resolver: zodResolver(editProjectSchema),
  });

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, project: Project) => {
    e.stopPropagation();
    setMenuAnchor({ el: e.currentTarget, project });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEditClick = () => {
    if (menuAnchor) {
      setSelectedProject(menuAnchor.project);
      reset({
        name: menuAnchor.project.name,
        description: menuAnchor.project.description || '',
        domain: menuAnchor.project.domain || '',
      });
      setEditDialogOpen(true);
      handleMenuClose();
    }
  };

  const handleDeleteClick = () => {
    if (menuAnchor) {
      setSelectedProject(menuAnchor.project);
      setDeleteDialogOpen(true);
      handleMenuClose();
    }
  };

  const handleCopyApiKey = async (apiKey: string, projectId: string) => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopiedId(projectId);
      showSuccess('API key copied to clipboard');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      showError('Failed to copy API key');
    }
  };

  const handleEditSubmit = async (data: EditProjectFormData) => {
    if (!organization || !selectedProject) return;

    setSaving(true);
    try {
      await organizationsService.updateProject(organization.id, selectedProject.id, {
        name: data.name,
        description: data.description || '',
        domain: data.domain || '',
      });

      await refreshProjects();
      showSuccess('Project updated successfully');
      setEditDialogOpen(false);
      setSelectedProject(null);
    } catch (error) {
      console.error('Error updating project:', error);
      showError('Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!organization || !selectedProject) return;

    // Don't allow deleting the last project
    if (projects.length <= 1) {
      showError('Cannot delete the last project');
      return;
    }

    setSaving(true);
    try {
      await organizationsService.deleteProject(organization.id, selectedProject.id);

      // If we deleted the current project, switch to another one
      if (currentProject?.id === selectedProject.id && projects.length > 1) {
        const otherProject = projects.find((p) => p.id !== selectedProject.id);
        if (otherProject) {
          await setCurrentProject(otherProject.id);
        }
      }

      await refreshProjects();
      showSuccess('Project deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedProject(null);
    } catch (error) {
      console.error('Error deleting project:', error);
      showError('Failed to delete project');
    } finally {
      setSaving(false);
    }
  };

  const canEdit = can('project:settings');
  const canDelete = can('project:delete');

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Projects
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your organization's projects. Each project has its own widget, tickets, and settings.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            label={`${projects.length}${projectLimit !== -1 ? ` / ${projectLimit}` : ''} projects`}
            size="small"
            color={atLimit ? 'warning' : 'default'}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddDialogOpen(true)}
            disabled={atLimit || !canEdit}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
              },
            }}
          >
            Add Project
          </Button>
        </Box>
      </Box>

      {atLimit && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You've reached your plan limit of {projectLimit} project{projectLimit !== 1 ? 's' : ''}.
          Upgrade your plan to add more projects.
        </Alert>
      )}

      {/* Projects Grid */}
      <Grid container spacing={3}>
        {projects.map((proj) => (
          <Grid key={proj.id} size={{ xs: 12, sm: 6, lg: 4 }}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: currentProject?.id === proj.id ? '2px solid' : '1px solid',
                borderColor: currentProject?.id === proj.id ? 'primary.main' : 'divider',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 3,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" fontWeight="medium">
                      {proj.name}
                    </Typography>
                    {currentProject?.id === proj.id && (
                      <Chip label="Active" size="small" color="primary" sx={{ height: 20 }} />
                    )}
                  </Box>
                  {canEdit && (
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, proj)}>
                      <MoreVert fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {proj.description || 'No description'}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                      Slug:
                    </Typography>
                    <Chip label={proj.slug} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                  </Box>

                  {proj.domain && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                        Domain:
                      </Typography>
                      <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                        {proj.domain}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                      API Key:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          bgcolor: 'grey.100',
                          px: 0.5,
                          borderRadius: 0.5,
                        }}
                      >
                        {proj.apiKey?.slice(0, 12)}...
                      </Typography>
                      <Tooltip title={copiedId === proj.id ? 'Copied!' : 'Copy API key'}>
                        <IconButton size="small" onClick={() => handleCopyApiKey(proj.apiKey, proj.id)}>
                          {copiedId === proj.id ? (
                            <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
                          ) : (
                            <ContentCopy sx={{ fontSize: 14 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </CardContent>

              <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                {currentProject?.id !== proj.id && (
                  <Button size="small" onClick={() => setCurrentProject(proj.id)}>
                    Switch to this project
                  </Button>
                )}
                <Button
                  size="small"
                  startIcon={<Code />}
                  onClick={() => {
                    setCurrentProject(proj.id);
                    window.location.href = '/app/settings/widget';
                  }}
                >
                  Widget
                </Button>
                {organization?.slug && (
                  <Tooltip title="Open portal">
                    <IconButton
                      size="small"
                      href={`/p/${organization.slug}`}
                      target="_blank"
                    >
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Context Menu */}
      <Menu anchorEl={menuAnchor?.el} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditClick}>
          <Edit sx={{ mr: 1 }} fontSize="small" /> Edit
        </MenuItem>
        {canDelete && projects.length > 1 && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <Delete sx={{ mr: 1 }} fontSize="small" /> Delete
          </MenuItem>
        )}
      </Menu>

      {/* Add Project Dialog */}
      <AddProjectDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
      />

      {/* Edit Project Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent>
          <Box component="form" id="edit-project-form" onSubmit={handleSubmit(handleEditSubmit)} sx={{ pt: 1 }}>
            <TextField
              {...register('name')}
              label="Project Name"
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name?.message}
              sx={{ mb: 2 }}
            />
            <TextField
              {...register('description')}
              label="Description"
              fullWidth
              multiline
              rows={2}
              error={!!errors.description}
              helperText={errors.description?.message}
              sx={{ mb: 2 }}
            />
            <TextField
              {...register('domain')}
              label="Domain"
              fullWidth
              error={!!errors.domain}
              helperText={errors.domain?.message || 'The domain where the widget is embedded'}
              placeholder="https://myapp.com"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-project-form"
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography>
            Are you sure you want to delete <strong>{selectedProject?.name}</strong>? All tickets, articles, and settings associated with this project will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving}
          >
            {saving ? 'Deleting...' : 'Delete Project'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
